import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ClipboardCheck, 
  Search, 
  Filter,
  Plus,
  Clock,
  Building2,
  ChevronRight,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuditList = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    fetchAudits();
  }, []);

  const fetchAudits = async () => {
    try {
      const response = await axios.get(`${API}/audits`);
      setAudits(response.data);
    } catch (error) {
      console.error('Failed to fetch audits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (auditId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this audit?')) return;
    
    try {
      await axios.delete(`${API}/audits/${auditId}`);
      setAudits(audits.filter(a => a.id !== auditId));
      toast.success('Audit deleted');
    } catch (error) {
      toast.error('Failed to delete audit');
    }
  };

  const getRiskBadgeClass = (risk) => {
    switch (risk) {
      case 'high_risk': return 'risk-high';
      case 'medium_risk': return 'risk-medium';
      case 'acceptable': return 'risk-acceptable';
      case 'good': return 'risk-good';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const getRiskLabel = (risk) => {
    switch (risk) {
      case 'high_risk': return 'High Risk';
      case 'medium_risk': return 'Medium Risk';
      case 'acceptable': return 'Acceptable';
      case 'good': return 'Good';
      default: return risk;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredAudits = audits.filter(audit => {
    const matchesSearch = audit.site_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          audit.auditor_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || audit.status === statusFilter;
    const matchesType = typeFilter === 'all' || audit.audit_type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-list-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Chivo'] text-slate-900">Audits</h1>
          <p className="text-slate-500 mt-1">View and manage all audit records</p>
        </div>
        <Button 
          onClick={() => navigate('/audits/new')} 
          className="btn-primary flex items-center gap-2"
          data-testid="new-audit-btn"
        >
          <Plus className="w-4 h-4" />
          New Audit
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by site or auditor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <div className="flex gap-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-40" data-testid="type-filter">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="central_kitchen">Central Kitchen</SelectItem>
                  <SelectItem value="site">Site</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Audit List */}
      {filteredAudits.length > 0 ? (
        <div className="space-y-4">
          {filteredAudits.map((audit) => (
            <Card 
              key={audit.id}
              className="cursor-pointer card-hover"
              onClick={() => navigate(audit.status === 'in_progress' ? `/audits/${audit.id}/conduct` : `/audits/${audit.id}`)}
              data-testid={`audit-card-${audit.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-slate-100 rounded-xl">
                      <Building2 className="w-6 h-6 text-slate-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{audit.site_name}</h3>
                      <p className="text-sm text-slate-500">{audit.site_location}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <Badge variant="outline" className="text-xs">
                          {audit.audit_type === 'central_kitchen' ? 'Central Kitchen' : 'Site'}
                        </Badge>
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(audit.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-2xl font-bold font-['Chivo'] text-slate-900">
                        {audit.score_percentage}%
                      </p>
                      <Badge className={`${getRiskBadgeClass(audit.risk_level)} border-0`}>
                        {getRiskLabel(audit.risk_level)}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Badge variant={audit.status === 'completed' ? 'default' : 'secondary'}>
                        {audit.status === 'completed' ? 'Completed' : 'In Progress'}
                      </Badge>
                      {isAdmin() && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={(e) => handleDelete(audit.id, e)}
                          data-testid={`delete-audit-${audit.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-16 text-center">
            <ClipboardCheck className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700">No audits found</h3>
            <p className="text-slate-500 mt-1">
              {searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Start by creating your first audit'}
            </p>
            {!searchTerm && statusFilter === 'all' && typeFilter === 'all' && (
              <Button 
                className="mt-4 btn-primary"
                onClick={() => navigate('/audits/new')}
                data-testid="create-first-audit-btn"
              >
                Create Audit
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AuditList;
